import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  UniqueIdentifier,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import IconButton from "../../elements/IconButton";
import useCloudPermissions from "../../hooks/useCloudPermissions";
import { useGoToGlobalPage } from "../Drawers/CreateQuickCreateDrawer/CreateFromGlobalPage";
import classNames from "classnames";
import { CSSProperties, useEffect, useState } from "react";
import { Button } from "reactstrap";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import AppTooltip from "../../elements/AppTooltip";

export type type = "Quick Create" | "Layout";

interface Props<T> {
  idKey?: Extract<keyof T, string>;
  nameKey?: Extract<keyof T, string>;
  items: T[];
  itemKey: string | undefined;
  onReorder?: ({ key, newOrder }: { key?: string; newOrder: string[] }) => void;
  title?: string;
  description?: string;
  onAdd?: () => void;
  onEdit?: (item: T) => void;
  onDelete?: ({ key, id }: { key?: string; id: string }) => void;
  onSaveAsDefault?: (id: string) => void;
  onSaveAsGlobal?: (item: T) => void;
  type?: type;
  disableDrag?: boolean;
  savedItemID?: string;
  globalKey?: keyof T;
  descriptionCSS?: string;
  isStringArray?: boolean;
  itemNameMap?: { [key: string]: string };
  useTooltip?: boolean;
  getColor?: (item: T) => string;
  copyable?: boolean;
}

export default function ManageItems<T>({
  idKey = "id" as Extract<keyof T, string>,
  nameKey = "Name" as Extract<keyof T, string>,
  items,
  itemKey,
  onReorder,
  onEdit,
  onDelete,
  onAdd,
  title,
  description = "No items have been added.",
  descriptionCSS,
  onSaveAsDefault,
  onSaveAsGlobal,
  type,
  savedItemID,
  globalKey,
  isStringArray,
  itemNameMap,
  useTooltip,
  getColor,
  copyable,
}: Props<T>) {
  const [itemIds, setItemIds] = useState<UniqueIdentifier[]>([]);

  useEffect(() => {
    const savedItemIds = isStringArray
      ? structuredClone(items)
      : items.map((item) => item[idKey]);

    try {
      if (JSON.stringify(savedItemIds) === JSON.stringify(itemIds)) return;
    } catch {
      return setItemIds([]);
    }

    setItemIds(savedItemIds as UniqueIdentifier[]);
  }, [items, idKey]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      activationConstraint: {
        distance: 5,
      },
    })
  );

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          id={idKey}
          items={itemIds}
          strategy={verticalListSortingStrategy}
        >
          <div className="tw:flex tw:flex-col tw:gap-[6px]">
            {itemIds.map((itemID) => {
              if (!itemID) return null;

              let item;

              if (isStringArray) {
                item = {
                  [idKey]: itemID,
                  [nameKey]: itemNameMap ? itemNameMap[itemID] : itemID,
                } as T;
              } else {
                item = items.find((item) => item[idKey] === itemID);
              }

              if (!item) return null;

              return (
                <SortableGCItem
                  key={itemID}
                  id={itemID as string}
                  item={item}
                  name={item[nameKey] as string}
                  type={type}
                  onEdit={onEdit}
                  onDelete={onDelete ? handleDeleteItem : undefined}
                  onSaveAsDefault={onSaveAsDefault}
                  onSaveAsGlobal={onSaveAsGlobal}
                  isDefault={savedItemID === itemID}
                  isGlobal={globalKey ? !!item?.[globalKey] : false}
                  disableDrag={!onReorder}
                  useTooltip={useTooltip}
                  getColor={getColor}
                  copyable={copyable}
                />
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
      {(!items?.length || items.length === 0) && (
        <div
          className={`border bg-superlight p-2 mt--1 rounded ${descriptionCSS}`}
        >
          <div className="text-dark text-sm font-weight-500">{description}</div>
        </div>
      )}
      {onAdd && (
        <Button className="my-2" onClick={onAdd} outline block>
          Add {title}
        </Button>
      )}
    </>
  );

  function handleDragStart() {
    document.body.style.setProperty("cursor", "grabbing");
  }

  function handleDragEnd(event: DragEndEvent) {
    document.body.style.setProperty("cursor", null);

    const { active, over } = event;

    if (!onReorder || !over?.id || active.id === over.id) return;

    const oldIndex = itemIds.indexOf(active.id);
    const newIndex = itemIds.indexOf(over.id);

    const newItemIds = arrayMove(itemIds, oldIndex, newIndex);

    setItemIds(newItemIds);

    onReorder({
      key: itemKey,
      newOrder: newItemIds as string[],
    });
  }

  function handleDeleteItem(prefillID: string) {
    if (!onDelete) return;

    onDelete({ key: itemKey, id: prefillID });
  }
}

interface SortableItemProps<T> {
  id: string;
  name: string;
  item: T;
  onDelete?: (id: string) => void;
  onEdit?: (item: T) => void;
  removeHint?: string;
  isDefault?: boolean;
  isGlobal?: boolean;
  onSaveAsDefault?: (id: string) => void;
  onSaveAsGlobal?: (item: T) => void;
  disableDrag?: boolean;
  height?: number;
  draggableOnly?: boolean;
  type: type | undefined;
  isDragging?: boolean;
  useTooltip?: boolean;
  getColor?: (item: T) => string;
  copyable?: boolean;
}

export function SortableGCItem<T>({
  id,
  name,
  item,
  onEdit,
  onDelete,
  removeHint = "Delete Item",
  isDefault,
  isGlobal,
  onSaveAsDefault,
  onSaveAsGlobal,
  disableDrag,
  height = 40,
  draggableOnly,
  type,
  useTooltip,
  getColor,
  copyable,
}: SortableItemProps<T>) {
  const { cPermissions } = useCloudPermissions();
  const { goToGlobalPage } = useGoToGlobalPage(type);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: id });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: "relative",
    zIndex: isDragging ? 2147483647 : undefined,
  };

  const Name = (
    <span className="tw:pl-3 tw:text-dark tw:truncate tw:font-medium tw:text-sm">
      {name}
    </span>
  );

  const Item = (
    <div
      className={classNames(
        "tw:px-0 tw:py-0 tw:rounded-xl tw:border tw:border-superlight btn-oauth",
        {
          "bg-primary-selected": !disableDrag && isDragging,
        }
      )}
    >
      <div
        className="tw:flex tw:items-center tw:justify-between"
        style={{ minHeight: height }}
      >
        <div className="tw:truncate tw:text-dark tw:pl-0 tw:flex tw:items-center">
          {!disableDrag && (
            <div
              className="tw:h-full tw:rounded-l-xl tw:flex tw:items-center"
              style={{
                minHeight: height,
                backgroundColor: getColor ? getColor(item) : "#eeeeee",
              }}
            >
              <i
                style={{ fontSize: 22 }}
                className="mdi mdi-drag tw:px-[1px] tw:text-dark"
              />
            </div>
          )}
          {useTooltip && !isDragging ? (
            <AppTooltip
              useTooltip
              content={name}
              placement="autoVertical"
              delayOpen={500}
              className="tw:w-full tw:truncate"
            >
              {Name}
            </AppTooltip>
          ) : (
            Name
          )}
        </div>
        {!draggableOnly && (
          <div className="tw:flex">
            {onSaveAsDefault && (
              <IconButton
                onClick={() => onSaveAsDefault(id as string)}
                icon={isDefault ? "star" : "star-outline"}
                tooltipText={isDefault ? "Default" : "Save as default"}
                color="warning"
                className="mx-1"
              />
            )}
            {onSaveAsGlobal &&
              cPermissions.cloud_create_global_quick_create && (
                <IconButton
                  onClick={() => onSaveAsGlobal(item)}
                  icon="earth"
                  tooltipText={isGlobal ? "Global" : "Save as global"}
                  color={isGlobal ? "primary" : "secondary"}
                  className="mx-1"
                  disabled={!cPermissions.cloud_create_global_quick_create}
                />
              )}
            {copyable && <CopyButton text={name} />}
            {onEdit && (
              <IconButton
                onClick={handleEdit}
                icon="pencil"
                tooltipText="Edit Item"
                className="mx-1"
                disabled={
                  isGlobal && !cPermissions.cloud_create_global_quick_create
                }
              />
            )}
            {onDelete && (
              <IconButton
                onClick={() => onDelete(id as string)}
                icon="close"
                tooltipText={removeHint}
                color="danger"
                className="mx-1"
                disabled={
                  isGlobal && !cPermissions.cloud_create_global_quick_create
                }
              />
            )}
          </div>
        )}
      </div>
    </div>
  );

  if (disableDrag) return Item;

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {Item}
    </div>
  );

  function handleEdit() {
    if (isGlobal) return goToGlobalPage();

    onEdit && onEdit(item);
  }
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <IconButton
      onClick={() => {
        setCopied(true);
        navigator.clipboard.writeText(text);
        setTimeout(() => setCopied(false), 3000);
      }}
      icon={copied ? "check" : "content-copy"}
      tooltipText={copied ? "Copied!" : "Copy Text"}
      className="tw:-mr-1 tw:ml-2 tw:text-medium"
    />
  );
}
